import { addDays, format, startOfWeek } from "date-fns";

/* ══════════════════════════════════════════════════════════════════════════
   Alert Calendar data layer.

   Shapes mirror /api/v1/scheduler/alert-calendar exactly. Entries are derived
   server-side from (review date x alert rule), and every day splits them by
   what they MEAN — a document is in exactly one array per day:

     day 0        newAlerts   the alert is raised
     day 1..N-1   reminders   restated, counting down
     day N        reviewDue   the review falls due

   The API also owns grid padding via `inRange`, so the UI renders `days` in
   order and adds no padding logic of its own.
   ══════════════════════════════════════════════════════════════════════════ */

export type EntryType = "NEW_ALERT" | "REMINDER" | "REVIEW_DUE";

export interface CalendarEntry {
  documentId: string;
  title: string;
  /** A review falls due against a specific version, so the row says which. */
  version: number;
  documentCategory: string;
  documentType: string;
  orgId: string;
  orgName: string;
  entryType: EntryType;
  entryDate: string;
  /** 0 on the alert date, totalDays on the review date */
  dayIndex: number;
  /** Measured, not configured — a MONTHS lead spans 28-31 days */
  totalDays: number;
  daysRemaining: number;
  alertDate: string;
  reviewDate: string;
  /** Null for rows published before the column existed. */
  publishedDate: string | null;
  /** reviewDate = publishedDate + reviewCycleDays. Reported, never read. */
  reviewCycleDays: number;
  leadAmount: number;
  leadUnit: string;
  alertSummary: string;
}

export interface CalendarDay {
  date: string;
  today: boolean;
  /** false for leading/trailing cells from the neighbouring month */
  inRange: boolean;
  newAlerts: CalendarEntry[];
  reminders: CalendarEntry[];
  reviewDue: CalendarEntry[];
  newAlertCount: number;
  reminderCount: number;
  reviewDueCount: number;
  totalCount: number;
}

export interface CalendarSummary {
  allActive: number;
  dueToday: number;
  dueThisWeek: number;
  dueThisMonth: number;
}

export interface CalendarPayload {
  view: string;
  anchorDate: string;
  rangeStart: string;
  rangeEnd: string;
  summary: CalendarSummary;
  days: CalendarDay[];
}

/**
 * GET /entry/{documentId}/{date} — the detail panel.
 *
 * Carries exactly what the list rows do not: the org path, the owner, and the
 * rule that produced the entry, plus a server-rendered headline and note.
 */
export interface EntryDetail extends CalendarEntry {
  headline: string;
  progressNote: string;
  today: boolean;
  orgPath: string;
  owner: string;
  alertConfigId: string;
  alertRule: string;
}

/* ── Filter options ─────────────────────────────────────────────────────── */

/* Only GOVERNANCE documents can appear — supporting evidence has no review
   date — so Governance is the sole option and the default selection. */
export const CATEGORY_OPTIONS = [{ value: "GOVERNANCE", label: "Governance" }];

/** The category the page opens on. */
export const DEFAULT_CATEGORY = CATEGORY_OPTIONS[0].value;

export const DOCUMENT_TYPE_OPTIONS = [
  { value: "POLICY", label: "Policy" },
  { value: "STANDARD", label: "Standard" },
  { value: "PROCEDURE", label: "Procedure" },
  { value: "GUIDELINE", label: "Guideline" },
];

/* ── Status tiles ───────────────────────────────────────────────────────── */

/* These count DOCUMENTS, not cells — a document inside a 10-day window is one
   active alert, not ten. A document can appear in more than one tile. */
export type SummaryKey = keyof CalendarSummary;

export const SUMMARY_TILES: {
  key: SummaryKey;
  label: string;
  tone: string;
  dot: string;
}[] = [
  {
    key: "allActive",
    label: "All Active",
    tone: "text-foreground",
    dot: "bg-muted-foreground",
  },
  {
    key: "dueToday",
    label: "Due Today",
    tone: "text-amber-600 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  {
    key: "dueThisWeek",
    label: "Due This Week",
    tone: "text-blue-600 dark:text-blue-400",
    dot: "bg-blue-500",
  },
  {
    key: "dueThisMonth",
    label: "Due This Month",
    tone: "text-indigo-600 dark:text-indigo-400",
    dot: "bg-indigo-500",
  },
];

/* ── Helpers ────────────────────────────────────────────────────────────── */

export const EMPTY_SUMMARY: CalendarSummary = {
  allActive: 0,
  dueToday: 0,
  dueThisWeek: 0,
  dueThisMonth: 0,
};

/** Parse `yyyy-MM-dd` as local midnight, avoiding UTC drift. */
export function parseApiDate(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export const toApiDate = (d: Date) => format(d, "yyyy-MM-dd");

/**
 * "2026-08-05" -> "05/08/2026", the format the UI presents dates in.
 * Returns "-" when absent, and the raw value when it will not parse.
 */
export function toDisplayDate(value?: string | null): string {
  if (!value) return "-";
  const parsed = parseApiDate(value.slice(0, 10));
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, "dd/MM/yyyy");
}

/** "GOVERNANCE" -> "Governance", "REVIEW_DUE" -> "Review Due" */
export function toTitleCase(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * The window counter, straight from the API: dayIndex is 0 on the alert date
 * and totalDays on the review date.
 */
export function dayLabel(entry: CalendarEntry): string {
  return `Day ${entry.dayIndex} of ${entry.totalDays}`;
}

/**
 * Reminders are urgent once the remaining days fall below half the window.
 * Drives the dot colour on the reminder chip.
 */
export function isUrgentEntry(entry: CalendarEntry): boolean {
  return entry.daysRemaining <= Math.max(2, Math.floor(entry.totalDays / 2));
}

/** Every entry on a day, in the order the UI presents them. */
export function allEntries(day: CalendarDay): CalendarEntry[] {
  return [
    ...(day.reviewDue ?? []),
    ...(day.newAlerts ?? []),
    ...(day.reminders ?? []),
  ];
}

export function emptyDay(date: Date, today: Date, inRange: boolean): CalendarDay {
  return {
    date: toApiDate(date),
    today: toApiDate(date) === toApiDate(today),
    inRange,
    newAlerts: [],
    reminders: [],
    reviewDue: [],
    newAlertCount: 0,
    reminderCount: 0,
    reviewDueCount: 0,
    totalCount: 0,
  };
}

/**
 * A blank but correctly shaped grid, used while a request is in flight and
 * when the endpoint is unreachable. Mirrors the documented empty state.
 */
export function blankPayload(
  view: "MONTH" | "WEEK" | "DAY",
  cursor: Date,
  today: Date,
): CalendarPayload {
  const cells =
    view === "DAY" ? 1 : view === "WEEK" ? 7 : 42;
  const start =
    view === "DAY"
      ? cursor
      : view === "WEEK"
        ? startOfWeek(cursor)
        : startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));

  const days = Array.from({ length: cells }, (_, i) => {
    const d = addDays(start, i);
    const inRange = view !== "MONTH" || d.getMonth() === cursor.getMonth();
    return emptyDay(d, today, inRange);
  });

  return {
    view,
    anchorDate: toApiDate(cursor),
    rangeStart: days[0].date,
    rangeEnd: days[days.length - 1].date,
    summary: EMPTY_SUMMARY,
    days,
  };
}

/* ── Response validation ────────────────────────────────────────────────── */

export interface ApiIssue {
  field: string;
  detail: string;
}

/**
 * Sanity-checks a payload against the documented contract. Surfaced in the
 * page's diagnostics strip while the integration is being validated.
 */
export function inspectPayload(
  payload: CalendarPayload,
  expectedCells: number,
): ApiIssue[] {
  const issues: ApiIssue[] = [];
  const days = payload.days ?? [];

  if (days.length !== expectedCells)
    issues.push({
      field: "days",
      detail: `Expected ${expectedCells} cells, received ${days.length}.`,
    });

  const mismatched = days.filter(
    (d) =>
      (d.newAlerts?.length ?? 0) !== d.newAlertCount ||
      (d.reminders?.length ?? 0) !== d.reminderCount ||
      (d.reviewDue?.length ?? 0) !== d.reviewDueCount,
  );
  if (mismatched.length > 0)
    issues.push({
      field: "counts vs arrays",
      detail: `${mismatched.length} day(s) report counts that do not match their arrays (first: ${mismatched[0].date}). Chips would disagree with the expanded rows.`,
    });

  /* A document must never sit in two arrays on the same day. */
  const doubled = days.find((d) => {
    const ids = allEntries(d).map((e) => e.documentId);
    return new Set(ids).size !== ids.length;
  });
  if (doubled)
    issues.push({
      field: "entryType",
      detail: `${doubled.date} lists the same document in more than one array.`,
    });

  if (days.length > 0 && !days.some((d) => d.today))
    issues.push({
      field: "today",
      detail: "No cell is flagged today; the current-day highlight will not render.",
    });

  return issues;
}
